import { forwardRef } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import type { ThemeName } from '../../themes/theme.types';
import { themes, themeNames } from '../../themes/themes.config';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Variante di visualizzazione
 */
export type ThemeSwitcherVariant = 'dropdown' | 'inline' | 'compact';

export interface ThemeSwitcherProps extends BaseComponentProps {
  /**
   * Variante di visualizzazione
   * @default 'dropdown'
   */
  variant?: ThemeSwitcherVariant;
  /**
   * Mostra preview colori tema
   * @default true
   */
  showPreview?: boolean;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ThemeSwitcher Component
 * 
 * Componente per cambiare tema dell'applicazione.
 * Disponibile in diverse varianti di visualizzazione.
 * 
 * @example
 * ```tsx
 * <ThemeSwitcher variant="dropdown" showPreview />
 * <ThemeSwitcher variant="inline" />
 * <ThemeSwitcher variant="compact" size="sm" />
 * ```
 */
export const ThemeSwitcher = forwardRef<HTMLDivElement, ThemeSwitcherProps>(
  (
    {
      className,
      variant = 'dropdown',
      showPreview = true,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const { theme: currentTheme, setTheme, themes: availableThemes } = useTheme();
    
    // SSR safety: usa themeNames come fallback
    const safeThemes = availableThemes || themeNames;
    
    // Durante SSR, non renderizzare nulla se siamo in ambiente server
    if (typeof window === 'undefined') {
      return null;
    }

    const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };

    // Dropdown variant
    if (variant === 'dropdown') {
      return (
        <div ref={ref} className={cn('relative inline-block', className)} {...props}>
          <select
            value={currentTheme}
            onChange={(e) => setTheme(e.target.value as ThemeName)}
            className={cn(
              'px-4 py-2 pr-8 rounded-md border border-gray-300',
              'bg-white text-gray-900 cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
              'hover:border-gray-400 transition-colors',
              sizeClasses[size],
              className
            )}
          >
            {safeThemes.map((themeName) => (
              <option key={themeName} value={themeName}>
                {themes[themeName].displayName}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // Inline variant (griglia di bottoni)
    if (variant === 'inline') {
      return (
        <div
          ref={ref}
          className={cn(
            'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3',
            className
          )}
          {...props}
        >
          {safeThemes.map((themeName) => {
            const theme = themes[themeName];
            const isActive = currentTheme === themeName;

            return (
              <button
                key={themeName}
                onClick={() => setTheme(themeName)}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all',
                  'hover:scale-105 hover:shadow-md',
                  isActive
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                  sizeClasses[size]
                )}
              >
                {showPreview && (
                  <div className="flex gap-1">
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: theme.colors.primary }}
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: theme.colors.secondary }}
                    />
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: theme.colors.accent }}
                    />
                  </div>
                )}
                <span className={cn('font-medium', isActive && 'text-blue-600')}>
                  {theme.displayName}
                </span>
              </button>
            );
          })}
        </div>
      );
    }

    // Compact variant (icone rotonde)
    if (variant === 'compact') {
      return (
        <div
          ref={ref}
          className={cn('flex flex-wrap gap-2', className)}
          {...props}
        >
          {safeThemes.map((themeName) => {
            const theme = themes[themeName];
            const isActive = currentTheme === themeName;

            return (
              <button
                key={themeName}
                onClick={() => setTheme(themeName)}
                title={theme.displayName}
                className={cn(
                  'w-8 h-8 rounded-full border-2 transition-all',
                  'hover:scale-110 hover:shadow-md',
                  isActive ? 'border-blue-500 shadow-md ring-2 ring-blue-200' : 'border-gray-300',
                  size === 'sm' && 'w-6 h-6',
                  size === 'lg' && 'w-10 h-10'
                )}
                style={{
                  background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 50%, ${theme.colors.accent} 100%)`,
                }}
              >
                <span className="sr-only">{theme.displayName}</span>
              </button>
            );
          })}
        </div>
      );
    }

    return null;
  }
);

ThemeSwitcher.displayName = 'ThemeSwitcher';

